import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import apiClient from "../../services/api";

const FONT = "'Inter', sans-serif";

const KPI_COLOR: Record<string, string> = {
  IN_PROGRESS: "#22C55E",
  PENDING:     "#9CA3AF",
  NOT_STARTED: "#9CA3AF",
  AT_RISK:     "#F97316",
  DELAYED:     "#EF4444",
  BLOCKED:     "#7C3AED",
};

interface KpiRow {
  label: string;
  count: number;
  total_value_usd: number;
}

const shortLabel = (s: string): string => {
  const key = s.toUpperCase().trim().replaceAll(/[\s_]+/g, "_");
  if (key === "NOT_STARTED" || key === "PENDING") return "Pending";
  return s.replaceAll("_", " ").toLowerCase().replaceAll(/\b\w/g, (c) => c.toUpperCase());
};

const fmtValue = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
};

const ShipmentValueDistribution: React.FC = () => {
  const [kpis, setKpis] = useState<KpiRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiClient
      .get<{ kpis: KpiRow[] }>("/api/databricks/dashboard/kpis")
      .then((res) => {
        const rows = (res.data.kpis ?? []).filter(
          (k) => k.label.toUpperCase() !== "COMPLETED" && k.total_value_usd > 0
        );
        setKpis(rows);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const chartData = kpis.map((k) => ({
    name: shortLabel(k.label),
    value: k.total_value_usd,
    color: KPI_COLOR[k.label.toUpperCase()] ?? "#94A3B8",
  }));

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: "12px",
        border: "1px solid #EAEAEA",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        p: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT,
          fontWeight: 600,
          fontSize: "1rem",
          color: "#111111",
          mb: 3,
        }}
      >
        Shipment Value Distribution by Status
      </Typography>

      {loading && (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={28} sx={{ color: "#22C55E" }} />
        </Box>
      )}

      {!loading && (error || chartData.length === 0) && (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontFamily: FONT, fontSize: "0.875rem", color: "#9CA3AF" }}>
            No data available
          </Typography>
        </Box>
      )}

      {!loading && !error && chartData.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Donut Chart */}
          <Box sx={{ width: 160, height: 160, flexShrink: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  stroke="none"
                >
                  {chartData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => [fmtValue(v), "Value"]} />
              </PieChart>
            </ResponsiveContainer>
          </Box>

          {/* Legend */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {chartData.map((item) => (
              <Box
                key={item.name}
                sx={{ display: "flex", alignItems: "center", gap: 3 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: item.color, flexShrink: 0 }} />
                  <Typography sx={{ fontFamily: FONT, fontSize: "0.875rem", color: "#374151" }}>
                    {item.name}
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: FONT, fontSize: "0.875rem", fontWeight: 700, color: "#111827" }}>
                  {fmtValue(item.value)}
                </Typography>
              </Box>
            ))}
          </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ShipmentValueDistribution;
